import React, {useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {toast} from 'react-toastify';
import {APIClient} from '../../utils/APIClient';
import {API_URL} from '../../settings/local_settings';
import {uploadWithProgress} from '../../utils/multipart';
import {Spinner} from '../Spinner';
import {FaIcon} from '../FaIcon';
import {LOGO_ACCEPTED_TYPES} from '../client/PortalAppearanceZone';
import {logoRejectionKey, logoUploadErrorMessage} from '../client/panel_v11/clientLogoUpload';
import {AgencyFormFieldError} from './AgencyFormFieldError';

const DEFAULT_PRIMARY = '#FF470F';

// La pestaña Portal de cada cliente lee la marca de la agencia de `get-agency-data/` con el caché de 30 s de
// `APIClient`: después de cambiarla acá, ese pedido tiene que volver a salir (review C-iv-icono-y-marca-03).
export function forgetAgencyBrandCache() {
    APIClient.clearCache('get-agency-data/');
}
const DEFAULT_SECONDARY = '#440631';

/**
 * «Marca para clientes» en Perfil de la agencia (D13 de la tanda iVirtual, ronda 2, 09/10): la marca que
 * ven en su portal todos los clientes que no tienen una propia.
 *
 * Hasta esta ronda el logo era un campo de URL a mano, y por eso iVirtual cargó su marca cliente por
 * cliente: el único lugar con subida era el portal de cada cliente. Ahora el logo se SUBE y queda
 * guardado en el acto (`POST/DELETE agency/brand-logo/`, mismo storage y mismos rechazos que los logos
 * de clientes); pegar una URL sigue siendo posible detrás de «O usar la URL de una imagen», y eso, como
 * el nombre y los colores, se guarda con el botón general del perfil.
 *
 * El estado vive en `AgencyInfo` (el «Guardar» del perfil manda todo junto): este bloque recibe los
 * valores y avisa los cambios. `onLogoChange` actualiza el logo después de subirlo o quitarlo, para que
 * ese «Guardar» mande la URL vigente y no la de antes.
 */
export function AgencyBrandSection({brand, agencyName, errors, onChange, onLogoChange}) {
    const {t} = useTranslation();
    const fileInputRef = useRef(null);
    const [working, setWorking] = useState(false);
    const [dragging, setDragging] = useState(false);
    const [showUrlField, setShowUrlField] = useState(false);
    const logoUrl = brand.logo_url || '';

    const upload = (file) => {
        if (!file || working) return;
        const rejection = logoRejectionKey(file);
        if (rejection) {
            toast.error(t(rejection));
            return;
        }
        const formData = new FormData();
        formData.append('file', file);
        setWorking(true);
        uploadWithProgress(`${API_URL}agency/brand-logo/`, formData).then((response) => {
            setWorking(false);
            const url = response && response.status === 'OK' && response.data && response.data.logo_url;
            if (url) {
                onLogoChange(url);
                forgetAgencyBrandCache();
                toast.success(t('portal_tab.branding_logo_uploaded'));
            } else {
                toast.error(logoUploadErrorMessage(response, t));
            }
        }).catch(() => {
            setWorking(false);
            toast.error(logoUploadErrorMessage(null, t));
        });
    };

    // Quitar persiste en el acto y el archivo se borra (si ningún cliente lo usa): pregunta antes, igual
    // que el logo del portal de un cliente.
    const remove = () => {
        if (working || !window.confirm(t('agency_info.logo_remove_confirm'))) return;
        setWorking(true);
        new APIClient(`${API_URL}agency/brand-logo/`).call('DELETE').then((response) => {
            setWorking(false);
            if (response && response.status === 'OK') {
                onLogoChange('');
                forgetAgencyBrandCache();
                toast.success(t('portal_tab.branding_logo_removed'));
            } else {
                toast.error(t('agency_info.logo_remove_error'));
            }
        }).catch(() => {
            setWorking(false);
            toast.error(t('agency_info.logo_remove_error'));
        });
    };

    const handleDrop = (event) => {
        event.preventDefault();
        setDragging(false);
        upload(event.dataTransfer && event.dataTransfer.files && event.dataTransfer.files[0]);
    };

    const colorField = (name, labelKey, fallback) => (
        <div className="form-item">
            <label>{t(labelKey)}</label>
            <div className="brand-color-input">
                <input type="color" value={brand[name] || fallback}
                       onChange={(e) => onChange({target: {name, value: e.target.value}})}/>
                <input type="text" name={name} value={brand[name] || ''} onChange={onChange}
                       placeholder={fallback} maxLength={7}/>
                {brand[name] && (
                    <button type="button" className="brand-color-clear"
                            onClick={() => onChange({target: {name, value: ''}})}>
                        {t('agency_info.clear_color')}
                    </button>
                )}
            </div>
            <AgencyFormFieldError errors={errors || {}} fieldName={name}/>
        </div>
    );

    return (
        <div className="agency-brand" data-testid="agency-brand">
            <header className="box-header agency-brand__header">
                <h2 className="box-title">{t('agency_info.client_branding_title')}</h2>
            </header>
            <p className="agency-brand__intro">{t('agency_info.client_branding_intro')}</p>
            <div className="agency-brand__body">
                <div className="agency-brand__form">
                    <div className="form-item">
                        <label>{t('portal_tab.branding_logo')}</label>
                        <div className={`logo-dropzone${dragging ? ' -dragging' : ''}`}
                             onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                             onDragLeave={() => setDragging(false)}
                             onDrop={handleDrop}>
                            {logoUrl
                                ? <img className="logo-dropzone-preview" src={logoUrl} alt={t('portal_tab.branding_logo')}/>
                                : <div className="logo-dropzone-empty"><FaIcon iconName="image"/></div>}
                            <div className="logo-dropzone-actions">
                                {working ? <Spinner color="positive small"/> : (
                                    <>
                                        <button type="button" className="btn -alt -small" data-testid="agency-brand-upload"
                                                onClick={() => fileInputRef.current && fileInputRef.current.click()}>
                                            {logoUrl ? t('portal_tab.branding_logo_replace') : t('portal_tab.branding_logo_upload')}
                                        </button>
                                        {logoUrl && (
                                            <button type="button" className="logo-dropzone-remove"
                                                    data-testid="agency-brand-remove" onClick={remove}>
                                                {t('portal_tab.branding_logo_remove')}
                                            </button>
                                        )}
                                    </>
                                )}
                            </div>
                            <input ref={fileInputRef} type="file" accept={LOGO_ACCEPTED_TYPES} hidden tabIndex={-1}
                                   data-testid="agency-brand-input"
                                   onChange={(e) => { upload(e.target.files && e.target.files[0]); e.target.value = ''; }}/>
                        </div>
                        <p className="field-footnote logo-dropzone-hint">{t('portal_tab.branding_logo_hint')}</p>
                        <button type="button" className="logo-url-toggle" onClick={() => setShowUrlField((v) => !v)}>
                            {t('portal_tab.branding_logo_use_url')}
                        </button>
                        {showUrlField && (
                            <>
                                <label htmlFor="agency-logo-url">{t('agency_info.logo_url')}</label>
                                <input id="agency-logo-url" type="text" name="logo_url" placeholder="https://..."
                                       value={logoUrl} onChange={onChange}/>
                            </>
                        )}
                        <AgencyFormFieldError errors={errors || {}} fieldName="logo_url"/>
                    </div>
                    <div className="form-item">
                        <label>{t('agency_info.display_name_for_clients')}</label>
                        <input type="text" name="display_name_for_clients" placeholder={agencyName}
                               value={brand.display_name_for_clients || ''} onChange={onChange}/>
                        <AgencyFormFieldError errors={errors || {}} fieldName="display_name_for_clients"/>
                    </div>
                    {/* Uno por fila: al lado de la vista previa, dos columnas cortaban el código del color. */}
                    {colorField('brand_primary_color', 'agency_info.brand_primary_color', DEFAULT_PRIMARY)}
                    {colorField('brand_secondary_color', 'agency_info.brand_secondary_color', DEFAULT_SECONDARY)}
                </div>
                <AgencyBrandPreview brand={brand} agencyName={agencyName}/>
            </div>
        </div>
    );
}

/**
 * Cómo se ve la cabecera del portal con esta marca, en miniatura: el logo y el nombre sobre el color
 * primario (el tema por defecto, «Inmersivo») y una tarjeta con el botón en el primario y el acento en
 * el secundario. Cambia mientras se escribe, antes de guardar.
 */
export function AgencyBrandPreview({brand, agencyName}) {
    const {t} = useTranslation();
    const primary = brand.brand_primary_color || DEFAULT_PRIMARY;
    const secondary = brand.brand_secondary_color || DEFAULT_SECONDARY;
    const name = brand.display_name_for_clients || agencyName || '';
    return (
        <div className="agency-brand__preview" data-testid="agency-brand-preview">
            <div className="agency-brand__preview-label">
                <FaIcon iconName="eye"/>
                {t('portal_modules.live_preview_label')}
            </div>
            <div className="agency-brand__portal" aria-hidden="true">
                <div className="agency-brand__portal-header" style={{background: primary}}>
                    <span className="agency-brand__portal-logo">
                        {brand.logo_url ? <img src={brand.logo_url} alt=""/> : <FaIcon iconName="image"/>}
                    </span>
                    <span className="agency-brand__portal-name" data-testid="agency-brand-preview-name">{name}</span>
                    <span className="agency-brand__portal-nav"/>
                </div>
                <div className="agency-brand__portal-body">
                    <div className="agency-brand__portal-card" style={{borderTopColor: secondary}}>
                        <span className="agency-brand__portal-line"/>
                        <span className="agency-brand__portal-line -short"/>
                        <span className="agency-brand__portal-cta" style={{background: primary}}/>
                    </div>
                    <div className="agency-brand__portal-card" style={{borderTopColor: secondary}}>
                        <span className="agency-brand__portal-line"/>
                        <span className="agency-brand__portal-line -short"/>
                        <span className="agency-brand__portal-cta" style={{background: primary}}/>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default AgencyBrandSection;
